import React, { useMemo } from 'react';
import styled from 'styled-components';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GitHubIcon from '@mui/icons-material/GitHub';
import MailIcon from '@mui/icons-material/Mail';
import { useBio } from '../../contexts/BioContext';
import { useTranslations } from '../../contexts/TranslationsContext';
import { CircleFlag } from 'react-circle-flags';
import { setLanguage, getLanguage } from '../../firebase';
import packageInfo from '../../../package.json';
import './footer.css';

const FooterWrapper = styled.footer`
  color: ${({ theme }) => theme.text_primary};
`;

const Logo = styled.h1`
  color: ${({ theme }) => (theme.bg === '#FFFFFF' ? '#059669' : '#10B981')};
`;

const NavLink = styled.a`
  color: ${({ theme }) => theme.text_primary};

  &:hover {
    color: ${({ theme }) => theme.primary};
  }

  &::before {
    background-color: ${({ theme }) => theme.primary};
  }
`;

const SocialMediaIcon = styled.a`
  color: ${({ theme }) => theme.text_primary};

  &:hover {
    color: ${({ theme }) => theme.primary};
  }
`;

const FlagButton = styled.div`
  &:hover::before {
    border: 2px solid ${({ theme }) => theme.primary};
  }

  &::after {
    background-color: ${({ theme }) => theme.card_light};
    color: ${({ theme }) => theme.text_primary};
  }
`;

const SECTIONS = [
  'about',
  'skills',
  'experience',
  'projects',
  'education',
  'testimonials',
];

const Footer = React.memo(() => {
  const { bio } = useBio();
  const { translations } = useTranslations();
  const currentLanguage = getLanguage();
  const linkedinUrl = useMemo(() => bio?.linkedin || '', [bio]);
  const githubUrl = useMemo(() => bio?.github || '', [bio]);
  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const version = useMemo(() => packageInfo.version, []);

  const handleScroll = (e, sectionId) => {
    e.preventDefault();
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleLanguageChange = (lang) => {
    setLanguage(lang);

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });

    setTimeout(() => {
      window.scrollTo({
        top: 0,
        left: 0,
      });
    }, 400);
  };

  return (
    <div className="footer-container">
      <FooterWrapper className="footer-wrapper">
        <Logo className="footer-logo">
          {translations?.footer?.tagline ||
            'BUILDING RESILIENT FOUNDATIONS FOR A SCALABLE FUTURE'}
        </Logo>

        <div className="footer-center-grid">
          {SECTIONS.map((section) => {
            const sectionKey = section === 'about' ? 'about' : section;
            const sectionLabel =
              translations?.nav?.[sectionKey] ||
              section.charAt(0).toUpperCase() + section.slice(1);
            return (
              <div className="footer-link-item" key={section}>
                <NavLink
                  href={`/#${section}`}
                  onClick={(e) => handleScroll(e, section)}
                  className="footer-nav-link"
                >
                  {sectionLabel}
                </NavLink>
              </div>
            );
          })}
        </div>

        <div className="language-switcher">
          <FlagButton
            data-lang={translations?.languages?.english || 'English'}
            onClick={
              currentLanguage === 'en'
                ? undefined
                : () => handleLanguageChange('en')
            }
            className={`flag-button ${currentLanguage === 'en' ? 'disabled' : ''}`}
            style={{
              opacity: currentLanguage === 'en' ? 0.5 : 1,
              cursor: currentLanguage === 'en' ? 'not-allowed' : 'pointer',
              pointerEvents: currentLanguage === 'en' ? 'none' : 'auto',
            }}
          >
            <CircleFlag countryCode="gb" height={28} width={28} />
          </FlagButton>

          <FlagButton
            data-lang={translations?.languages?.arabic || 'العربية'}
            onClick={
              currentLanguage === 'ar'
                ? undefined
                : () => handleLanguageChange('ar')
            }
            className={`flag-button ${currentLanguage === 'ar' ? 'disabled' : ''}`}
            style={{
              opacity: currentLanguage === 'ar' ? 0.5 : 1,
              cursor: currentLanguage === 'ar' ? 'not-allowed' : 'pointer',
              pointerEvents: currentLanguage === 'ar' ? 'none' : 'auto',
            }}
          >
            <CircleFlag countryCode="sa" height={28} width={28} />
          </FlagButton>
        </div>

        <div className="social-media-icons">
          <SocialMediaIcon
            href={linkedinUrl}
            target="_blank"
            aria-label="LinkedIn profile"
            rel="noopener noreferrer"
            className="social-media-icon"
          >
            <LinkedInIcon />
          </SocialMediaIcon>
          <SocialMediaIcon
            href={githubUrl}
            target="_blank"
            aria-label="GitHub profile"
            rel="noopener noreferrer"
            className="social-media-icon"
          >
            <GitHubIcon />
          </SocialMediaIcon>
          <SocialMediaIcon
            href="mailto:farhatrkhalil@gmail.com"
            aria-label="Email"
            className="social-media-icon"
          >
            <MailIcon />
          </SocialMediaIcon>
        </div>

        <div className="copyright-container">
          <p>{translations?.footer?.name || bio?.name || 'Khalil Farhat'}</p>
          <b>
            &copy; {currentYear}{' '}
            {translations?.footer?.allRightsReserved || 'All Rights Reserved'} •
            v{version}
          </b>
        </div>
      </FooterWrapper>
    </div>
  );
});

Footer.displayName = 'Footer';

export default Footer;
